"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { navLinks, testNavLinks } from "@/lib/links";
import { sosmed } from "@/lib/sosmed";
import { cn } from "@/lib/tailwind-utils";
import { Menu } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  FaBriefcase,
  FaBuilding,
  FaBullseye,
  FaCertificate,
  FaChevronDown,
  FaClipboardList,
  FaEye,
  FaGraduationCap,
  FaInfoCircle,
  FaLifeRing,
  FaMoneyBillWave,
  FaPhone,
  FaSitemap,
} from "react-icons/fa";
import { MdAccountBalanceWallet, MdDashboard } from "react-icons/md";
import { RiAdminLine } from "react-icons/ri";
import SectionWrapper from "./section-wrapper";

const iconMap = {
  GraduationCap: FaGraduationCap,
  Certificate: FaCertificate,
  Dashboard: MdDashboard,
  Admin: RiAdminLine,
  Wallet: MdAccountBalanceWallet,
  Eye: FaEye,
  Briefcase: FaBriefcase,
  Phone: FaPhone,
  Money: FaMoneyBillWave,
  ClipboardList: FaClipboardList,
  Bullseye: FaBullseye,
  Building: FaBuilding,
  Sitemap: FaSitemap,
  LifeRing: FaLifeRing,
  InfoCircle: FaInfoCircle,
};

const colorMap = {
  GraduationCap: "from-amber-400 to-orange-600",
  Certificate: "from-blue-400 to-indigo-600",
  Dashboard: "from-purple-400 to-indigo-600",
  Admin: "from-rose-400 to-red-600",
  Wallet: "from-emerald-400 to-teal-600",
  Eye: "from-cyan-400 to-blue-600",
  Briefcase: "from-slate-400 to-slate-600",
  Phone: "from-blue-500 to-blue-700",
  Money: "from-green-400 to-emerald-600",
  ClipboardList: "from-orange-400 to-red-500",
  Bullseye: "from-red-500 to-rose-700",
  Building: "from-slate-500 to-slate-700",
  Sitemap: "from-indigo-500 to-blue-700",
};

export default function Navbar() {
  const [isOpenSheet, setIsOpenSheet] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed z-50 transition-all duration-700 ease-in-out",
        scrolled
          ? "top-0 left-0 right-0 h-[80px] bg-white/90 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.05)] border-b border-slate-100/80"
          : "top-0 left-0 right-0 h-[100px] bg-transparent border-transparent"
      )}
    >
      <SectionWrapper className="transition-all duration-500 max-w-[1440px]">
        <nav className="flex w-full items-center justify-between h-full relative">
          {/* LOGO SECTION - Left Aligned */}
          <div className="flex-1 flex items-center justify-start">
            <Link href="/" className="flex items-center gap-3 group">
              <div
                className={cn(
                  "relative transition-all duration-500 flex items-center group",
                  scrolled ? "h-[50px] w-[200px]" : "h-[80px] w-[280px]"
                )}
              >
                <div
                  className={cn(
                    "absolute inset-0 bg-blue-400/20 rounded-full blur-3xl transition-opacity duration-1000",
                    scrolled ? "opacity-0" : "opacity-100"
                  )}
                />

                <Image
                  src={"/images/silangit-new.png"}
                  fill
                  alt="SILANGIT"
                  priority
                  quality={100}
                  className="object-contain relative z-10 drop-shadow-[0_10px_10px_rgba(0,0,0,0.1)] group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </Link>
          </div>

          {/* DESKTOP MENU - Center Aligned */}
          <div className="hidden lg:flex items-center justify-center">
            {navLinks.map((link) => {
              if (link?.subLinks?.length > 0) {
                const isActive = link.subLinks.some(
                  (sub) => pathname === sub.href
                );

                return (
                  <DropdownMenu modal={false} key={link.id}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          "px-3 py-2 rounded-xl text-[16px] font-bold transition-all duration-300 flex items-center gap-2 outline-none whitespace-nowrap",
                          isActive
                            ? scrolled
                              ? "text-blue-600 bg-blue-50/80 backdrop-blur-md shadow-sm border border-blue-100/50"
                              : "text-white bg-white/15 backdrop-blur-xl shadow-lg ring-1 ring-white/30"
                            : scrolled
                            ? "text-slate-600 hover:text-blue-600 hover:bg-blue-50/60"
                            : "text-white/80 hover:text-white hover:bg-white/10"
                        )}
                      >
                        {link.name}
                        <FaChevronDown
                          className={cn(
                            "h-3 w-3 transition-transform duration-300 opacity-50",
                            isActive ? "rotate-180" : "group-hover:rotate-180"
                          )}
                        />
                      </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      align="start"
                      sideOffset={12}
                      className="w-[340px] mt-4 rounded-[24px] border border-white/40 shadow-[0_25px_70px_rgba(0,0,0,0.15)] p-0 bg-white/95 backdrop-blur-2xl overflow-hidden"
                    >
                      {/* HEADER */}
                      <div className="px-6 py-5 bg-gradient-to-br from-indigo-700 via-blue-600 to-indigo-800 relative overflow-hidden">
                        {/* DECORATIVE ELEMENTS */}
                        <div className="absolute top-[-20%] right-[-10%] w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse" />
                        <div className="absolute bottom-[-30%] left-[-10%] w-32 h-32 bg-blue-400/20 rounded-full blur-2xl" />
                        <div className="absolute top-8 left-12 w-2 h-2 bg-white/20 rounded-full animate-bounce" />
                        <div className="absolute bottom-8 right-12 w-3 h-3 bg-white/20 rounded-full" />

                        <div className="relative flex items-center gap-4">
                          <div className="w-12 h-12 rounded-[18px] bg-white/15 backdrop-blur-xl flex items-center justify-center border border-white/25 shadow-inner group-hover:rotate-6 transition-transform duration-500">
                             {link.headerIcon && iconMap[link.headerIcon] ? (
                               (() => {
                                 const Icon = iconMap[link.headerIcon];
                                 return <Icon size={22} className="text-white drop-shadow-md" />;
                               })()
                             ) : (
                               <MdAccountBalanceWallet size={24} className="text-white" />
                             )}
                          </div>
                          
                          <div>
                            <h4 className="text-lg font-black text-white tracking-tight">
                              {link.name}
                            </h4>
                            <p className="text-white/70 text-[11px] font-medium tracking-wide flex items-center gap-1.5 uppercase">
                              <span className="w-1 h-1 rounded-full bg-blue-400" />
                              Explore semua layanan kami
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* LINKS GRID */}
                      <div className="p-2 bg-slate-50/50">
                        {link.subLinks.map((sublink) => {
                          const isCurrent = pathname === sublink.href;
                          const gradientClass = colorMap[sublink.icon] || "from-blue-500 to-indigo-600";
                          const iconColorClass = gradientClass.split(' ')[1].replace('to-', 'text-');

                          return (
                            <DropdownMenuItem
                              key={sublink.id}
                              asChild
                              className="mb-1 last:mb-0 focus:bg-transparent"
                            >
                              <Link
                                href={sublink.href}
                                className={cn(
                                  "group/item cursor-pointer rounded-[18px] px-3.5 py-3 flex items-center gap-4 transition-all duration-300 relative",
                                  isCurrent
                                    ? "bg-blue-50/50 shadow-sm border border-blue-100/50"
                                    : "hover:bg-white hover:shadow-[0_10px_30px_rgba(0,0,0,0.05)] border border-transparent hover:border-white"
                                )}
                              >
                                {isCurrent && (
                                  <div className="absolute left-0 top-3 bottom-3 w-1 bg-blue-600 rounded-r-full" />
                                )}

                                <div
                                  className={cn(
                                    "relative shrink-0 w-10 h-10 rounded-[14px] flex items-center justify-center transition-all duration-300 overflow-hidden",
                                    isCurrent
                                      ? cn("bg-gradient-to-br text-white shadow-md", gradientClass)
                                      : "bg-slate-100 shadow-sm group-hover/item:text-white"
                                  )}
                                >
                                  {/* THEMED BACKGROUND FOR NON-ACTIVE */}
                                  {!isCurrent && (
                                    <div className={cn(
                                      "absolute inset-0 opacity-10 bg-gradient-to-br",
                                      gradientClass
                                    )} />
                                  )}

                                  {/* HOVER GRADIENT BACKGROUND */}
                                  <div className={cn(
                                    "absolute inset-0 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 bg-gradient-to-br",
                                    gradientClass
                                  )} />
                                  
                                  {sublink.icon && iconMap[sublink.icon] ? (
                                    (() => {
                                      const Icon = iconMap[sublink.icon];
                                      return (
                                        <Icon 
                                          size={18} 
                                          className={cn(
                                            "relative z-10 transition-all duration-300 group-hover/item:scale-110 group-hover/item:rotate-12",
                                            !isCurrent ? iconColorClass : "text-white",
                                            "group-hover/item:text-white"
                                          )} 
                                        />
                                      );
                                    })()
                                  ) : (
                                    <div className="w-1.5 h-1.5 rounded-full bg-current relative z-10" />
                                  )}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <h5 className="text-[14.5px] font-bold text-slate-800 group-hover/item:text-blue-700 transition-colors">
                                    {sublink.name}
                                  </h5>

                                  <p className="text-[11px] text-slate-500 line-clamp-1 font-medium mt-0.5">
                                    {sublink.desc || "Informasi dan layanan portal SILANGIT"}
                                  </p>
                                </div>

                                <div className="opacity-0 group-hover/item:opacity-100 transition-all duration-300 -translate-x-2 group-hover/item:translate-x-0">
                                  <FaChevronDown className="-rotate-90 text-slate-300 text-[10px]" />
                                </div>
                              </Link>
                            </DropdownMenuItem>
                          );
                        })}
                      </div>

                      {/* FOOTER */}
                      <div className="px-6 py-4 bg-white border-t border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full bg-blue-500 animate-pulse" />

                          <span className="text-[12px] text-slate-500 font-bold uppercase tracking-wider">
                            Status: Online
                          </span>
                        </div>

                        <p className="text-[11px] text-slate-400 font-medium italic">
                          Powered by SILANGIT
                        </p>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              const isActive = pathname === link.href;

              return (
                <Link
                  key={link.id}
                  href={link.href}
                  className={cn(
                    "px-3 py-2 rounded-xl text-[16px] font-bold transition-all duration-300 relative whitespace-nowrap",
                    isActive
                      ? scrolled
                        ? "text-blue-600 bg-blue-50/80 backdrop-blur-md shadow-sm border border-blue-100/50"
                        : "text-white bg-white/15 backdrop-blur-xl shadow-lg ring-1 ring-white/30"
                      : scrolled
                      ? "text-slate-600 hover:text-blue-600 hover:bg-blue-50/60"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  )}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>

          {/* RIGHT SIDE SECTION - Right Aligned */}
          <div className="flex-1 flex items-center justify-end gap-3">
            {/* DESKTOP RIGHT CONTENT */}
            <div className="hidden lg:flex items-center gap-4">
              {/* SOCIAL */}
              <div className="flex items-center gap-1.5">
                {sosmed.map((data) => {
                  if (data?.name === "Tarif Layanan") return null;

                  if (!data.isHero) {
                    const Icon = data.icon;

                    return (
                      <Link
                        target="_blank"
                        key={data.id}
                        href={data.href}
                        title={data.name}
                        className={cn(
                          "group flex h-[40px] w-[40px] items-center justify-center rounded-full transition-all duration-500 hover:-translate-y-1 shadow-lg",
                          scrolled
                            ? "bg-gradient-to-br from-[#005CA1] to-[#0078CC] text-white"
                            : "bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20"
                        )}
                      >
                        <Icon className="h-[18px] w-[18px]" />
                      </Link>
                    );
                  }

                  return (
                    <Link
                      target="_blank"
                      key={data.id}
                      href={data.href}
                      title={data.name}
                      className={cn(
                        "group flex h-[40px] w-[40px] items-center justify-center rounded-full transition-all duration-500 hover:-translate-y-1 shadow-lg",
                        scrolled
                          ? "bg-gradient-to-br from-[#005CA1] to-[#0078CC]"
                          : "bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20"
                      )}
                    >
                      <Image
                        width={22}
                        height={22}
                        src={data.imageRef}
                        alt={data.name}
                        className="h-[20px] w-[20px] rounded-full object-cover"
                      />
                    </Link>
                  );
                })}
              </div>

              {/* CTA */}
              <Button
                asChild
                className="group rounded-[18px] bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 text-white font-black px-5 h-11 shadow-[0_10px_25px_-5px_rgba(37,99,235,0.4)] hover:shadow-[0_15px_35px_-5px_rgba(37,99,235,0.6)] transition-all duration-500 hover:-translate-y-1"
              >
                <Link href="/pelatihan">Daftar Sekarang</Link>
              </Button>
            </div>

            {/* MOBILE TRIGGER */}
            <div className="flex lg:hidden">
              <Sheet open={isOpenSheet} onOpenChange={setIsOpenSheet}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      "shrink-0 lg:hidden rounded-xl transition-all duration-200",
                      scrolled
                        ? "border-slate-200 text-slate-600 hover:bg-blue-50"
                        : "border-white/20 text-white hover:bg-white/10"
                    )}
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>

                <SheetContent
                  side="right"
                  className="bg-white/95 backdrop-blur-xl border-l border-slate-100 w-[280px] p-0"
                >
                  <nav className="flex flex-col h-full">
                <div className="px-6 py-5 border-b border-slate-100">
                  <Link
                    href="/"
                    className="flex items-center gap-2"
                    onClick={() => setIsOpenSheet(false)}
                  >
                    <div className="relative h-[42px] w-[42px]">
                      <Image
                        alt="BPPP Tegal"
                        src="/images/bpp_tegal_new.png"
                        fill
                        className="object-contain"
                      />
                    </div>

                    <div className="relative h-[40px] w-[160px]">
                      <Image
                        src={"/images/silangit.png"}
                        fill
                        alt="SILANGIT"
                        className="object-contain"
                      />
                    </div>
                  </Link>
                </div>

                <div className="flex-1 px-4 py-6 flex flex-col gap-2 overflow-y-auto">
                  {testNavLinks.map((link) => {
                    if (link?.subLinks?.length > 0) {
                      return (
                        <div key={link.id} className="flex flex-col gap-1">
                          <p className="px-4 text-[10px] font-bold text-blue-500 uppercase tracking-[0.2em] mt-4 mb-2">
                            {link.name}
                          </p>

                          {link.subLinks.map((linkSub) => (
                            <Link
                              key={linkSub.id}
                              href={linkSub.href}
                              onClick={() => setIsOpenSheet(false)}
                              className={cn(
                                "px-4 py-3 rounded-[16px] text-[15px] font-bold transition-all duration-300 flex items-center gap-3",
                                pathname === linkSub.href
                                  ? "text-blue-600 bg-blue-50 shadow-sm border border-blue-100"
                                  : "text-slate-600 hover:bg-white hover:text-blue-600"
                              )}
                            >
                              <div className={cn(
                                "w-8 h-8 rounded-xl flex items-center justify-center shrink-0",
                                pathname === linkSub.href ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"
                              )}>
                                {linkSub.icon && iconMap[linkSub.icon] ? (
                                  (() => {
                                    const Icon = iconMap[linkSub.icon];
                                    return <Icon size={16} />;
                                  })()
                                ) : (
                                  <div className="w-1.5 h-1.5 rounded-full bg-current" />
                                )}
                              </div>
                              {linkSub.name}
                            </Link>
                          ))}
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={link.id}
                        href={link.href}
                        onClick={() => setIsOpenSheet(false)}
                        className={cn(
                          "px-4 py-3 rounded-[16px] text-[15px] font-bold transition-all duration-300",
                          pathname === link.href
                            ? "text-blue-600 bg-blue-50 shadow-sm border border-blue-100"
                            : "text-slate-600 hover:bg-white hover:text-blue-600"
                        )}
                      >
                        {link.name}
                      </Link>
                    );
                  })}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
      </SectionWrapper>
    </header>
  );
}